import type { KpiSummary } from '@/types';
import { formatKvah, formatKwh, formatPF, formatRupees } from '@/lib/formatters';
import { Activity, IndianRupee, Zap, Gauge } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useLanguage } from '@/hooks/useLanguage';

interface KpiStripProps { summary: KpiSummary }

/* ── Section header ────────────────────────────────────────────────────── */
function RowHeader({ label, sub }: { label: string; sub: string }) {
  const { t } = useLanguage();
  return (
    <div className="flex items-baseline gap-2 px-0.5 pt-2 pb-1">
      <span className="text-[13px] font-black uppercase tracking-widest text-ink-900">{t(label)}</span>
      <span className="text-[10px] font-medium text-ink-400 normal-case">{t(sub)}</span>
    </div>
  );
}

/* ── Single KPI tile ───────────────────────────────────────────────────── */
function Tile({
  icon, label, value, colorClass, bg, border, warning,
}: {
  icon:       React.ReactNode;
  label:      string;
  value:      string;
  colorClass: string;
  bg:         string;
  border:     string;
  warning?:   string;
}) {
  const { t } = useLanguage();
  return (
    <div className={cn(
      'rounded-2xl border px-2.5 py-2.5 flex flex-col gap-1 min-w-0 overflow-hidden',
      'shadow-[0_1px_3px_rgba(15,23,42,0.06)]',
      bg, border,
    )}>
      {/* Label row */}
      <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-ink-700 leading-none truncate">
          {t(label)}
        </span>
        <span className={cn('shrink-0', colorClass)}>{icon}</span>
      </div>

      {/* Value — full formatted string, no unit prop duplication */}
      <span className={cn('num font-black leading-none mt-0.5 truncate block w-full', colorClass,
        'text-[clamp(12px,3.8vw,18px)]')}>
        {value}
      </span>

      {/* Optional warning */}
      {warning && (
        <span className="text-[9px] font-bold text-red-600 leading-none">{warning}</span>
      )}
    </div>
  );
}

function pfWarning(pf: number, t: (k: string) => string): string | undefined {
  if (pf <= 0) return undefined;
  if (pf < 0.90) return '⚠ ' + t('Penalty zone');
  if (pf < 0.92) return '⚠ ' + t('Near limit');
  return undefined;
}
function pfColor(pf: number) {
  if (pf <= 0)   return { colorClass: 'text-ink-400', bg: 'bg-slate-50', border: 'border-slate-200' };
  if (pf >= 0.95) return { colorClass: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200/60' };
  if (pf >= 0.90) return { colorClass: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200/60'  };
  return            { colorClass: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200/60'    };
}

const GREEN  = { colorClass: 'text-brand-700',  bg: 'bg-brand-50',  border: 'border-brand-200/60' };
const BLUE   = { colorClass: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200/60'  };
const COST   = { colorClass: 'text-ink-800',    bg: 'bg-slate-50',  border: 'border-slate-200'    };

export function KpiStrip({ summary }: KpiStripProps) {
  const { t } = useLanguage();

  return (
    <section className="space-y-0.5">

      {/* ── TODAY SO FAR ─────────────────────────────────────────── */}
      <RowHeader label="Today" sub="units consumed so far" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        <Tile {...GREEN} icon={<Zap className="w-3.5 h-3.5" />}
          label="Energy" value={formatKwh(summary.kwhToday)} />
        <Tile {...BLUE}  icon={<Gauge className="w-3.5 h-3.5" />}
          label="Apparent" value={formatKvah(summary.kvahToday)} />
        <Tile {...pfColor(summary.pfNow)}
          icon={<Activity className="w-3.5 h-3.5" />}
          label="Live Power Factor" value={formatPF(summary.pfNow)}
          warning={pfWarning(summary.pfNow, t)} />
        <Tile {...COST}  icon={<IndianRupee className="w-3.5 h-3.5" />}
          label="Cost" value={formatRupees(summary.costToday, { compact: true })} />
      </div>

      {/* ── YESTERDAY ────────────────────────────────────────────── */}
      <RowHeader label="Yesterday" sub="12 AM – 12 AM" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        <Tile {...GREEN} icon={<Zap className="w-3.5 h-3.5" />}
          label="Energy" value={formatKwh(summary.kwhYesterday)} />
        <Tile {...BLUE}  icon={<Gauge className="w-3.5 h-3.5" />}
          label="Apparent" value={formatKvah(summary.kvahYesterday)} />
        <Tile {...pfColor(summary.pfYesterday)}
          icon={<Activity className="w-3.5 h-3.5" />}
          label="Average Power Factor" value={formatPF(summary.pfYesterday)}
          warning={pfWarning(summary.pfYesterday, t)} />
        <Tile {...COST}  icon={<IndianRupee className="w-3.5 h-3.5" />}
          label="Cost" value={formatRupees(summary.costYesterday, { compact: true })} />
      </div>

      {/* ── THIS MONTH (MTD) ─────────────────────────────────────── */}
      <RowHeader label="This Month" sub="1st 12 AM → now" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        <Tile {...GREEN} icon={<Zap className="w-3.5 h-3.5" />}
          label="Energy" value={formatKwh(summary.kwhMtd)} />
        <Tile {...BLUE}  icon={<Gauge className="w-3.5 h-3.5" />}
          label="Apparent" value={formatKvah(summary.kvahMtd)} />
        <Tile {...pfColor(summary.pfMtd)}
          icon={<Activity className="w-3.5 h-3.5" />}
          label="Average Power Factor" value={formatPF(summary.pfMtd)}
          warning={pfWarning(summary.pfMtd, t)} />
        <Tile {...COST}  icon={<IndianRupee className="w-3.5 h-3.5" />}
          label="Cost" value={formatRupees(summary.costMtd, { compact: true })} />
      </div>

    </section>
  );
}